/**
 * Firm membership and firm clients.
 *
 * A founder creates the firm and is approved at once; everyone else requests to
 * join and waits for the owner. A client is added by consent: someone already on
 * the platform gets a request they approve in their app, anyone else an invite.
 */
import { gql } from '@apollo/client';

const MEMBER_FIELDS = `
  employeeId
  userId
  organizationId
  organizationName
  organizationType
  firmRole
  membershipStatus
  isOwner
  requestedAt
  reviewedAt
`;

export const CREATE_FIRM = gql`
  mutation CreateFirm($input: CreateFirmInput!) {
    createFirm(input: $input) { ${MEMBER_FIELDS} }
  }
`;

export const REQUEST_FIRM_MEMBERSHIP = gql`
  mutation RequestFirmMembership($organizationId: Int!, $firmRole: EmployeeFunctionalRoleEnum!) {
    requestFirmMembership(organizationId: $organizationId, firmRole: $firmRole) { ${MEMBER_FIELDS} }
  }
`;

export const APPROVE_FIRM_MEMBER = gql`
  mutation ApproveFirmMember($employeeId: Int!) {
    approveFirmMember(employeeId: $employeeId) { employeeId membershipStatus }
  }
`;

export const REJECT_FIRM_MEMBER = gql`
  mutation RejectFirmMember($employeeId: Int!) {
    rejectFirmMember(employeeId: $employeeId) { employeeId membershipStatus }
  }
`;

const ADD_FIRM_CLIENT_FIELDS = `
  inviteUrl
  emailSent
  client { linkId clientUserId clientName inviteEmail invitePhone status }
`;

export const ADD_FIRM_CLIENT = gql`
  mutation AddFirmClient($email: String, $phone: String) {
    addFirmClient(email: $email, phone: $phone) {
      ${ADD_FIRM_CLIENT_FIELDS}
      smsSent
    }
  }
`;

/**
 * The same write, without `smsSent`.
 *
 * `smsSent` is new: it reports whether the magic link was texted to a client
 * added by phone. A backend that predates it rejects the whole mutation at
 * validation — "Cannot query field 'smsSent'" — and refuses to add the client
 * at all, which is a working feature broken by an unasked-for one. So
 * `useAddFirmClient` sends this instead when it meets that refusal, and the
 * desk shares the invite link itself, exactly as it did before the field
 * existed.
 */
export const ADD_FIRM_CLIENT_WITHOUT_SMS = gql`
  mutation AddFirmClientWithoutSms($email: String, $phone: String) {
    addFirmClient(email: $email, phone: $phone) {
      ${ADD_FIRM_CLIENT_FIELDS}
    }
  }
`;
